import type { PlanResource, ScheduledPlanChange } from '@shared/organizations/domain/entities/Organization';

/**
 * Mapeo puro `scheduledPlanChange` (+ `scheduledPlanChangeFits`/`scheduledPlanChangeBlockedBy`) →
 * descriptor del banner global de cambio de plan programado en OrgLayout. Mismo molde que
 * `inactivityBanner`: no decide el estado de la organización, solo traduce un valor ya resuelto
 * por el backend a copy/CTA de presentación.
 *
 * Mutuamente excluyente con el banner de inactividad (D11 design.md): `scheduledPlanChange` solo
 * existe con `hasActiveSubscription: true`; `inactivityReason` solo existe con organización
 * inactiva. La exclusión la garantiza `OrgLayout` (guarda `organization.isActive()`), no este mapper.
 */

export type ScheduledPlanChangeBannerSeverity = 'info' | 'warn';

export interface ScheduledPlanChangeBannerDescriptor {
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  severity: ScheduledPlanChangeBannerSeverity;
  /** Recursos que exceden el plan destino programado — presente solo cuando `scheduledPlanChangeFits` es `false`. */
  blockedBy?: PlanResource[];
}

export function scheduledPlanChangeBanner(
  scheduledPlanChange: ScheduledPlanChange | null,
  scheduledPlanChangeFits: boolean | null,
  scheduledPlanChangeBlockedBy: PlanResource[] | null,
): ScheduledPlanChangeBannerDescriptor | null {
  if (!scheduledPlanChange) return null;

  if (scheduledPlanChangeFits === false) {
    return {
      titleKey: 'scheduled_plan_change.banner_title',
      bodyKey: 'scheduled_plan_change.banner_body',
      ctaKey: 'scheduled_plan_change.cancel_button',
      severity: 'warn',
      blockedBy: scheduledPlanChangeBlockedBy ?? [],
    };
  }

  return {
    titleKey: 'scheduled_plan_change.banner_title',
    bodyKey: 'scheduled_plan_change.banner_body',
    ctaKey: 'scheduled_plan_change.cancel_button',
    severity: 'info',
  };
}
